import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Skeleton,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const TopZipCodes = ({ data, loading, onViewBookkeepers }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [viewType, setViewType] = useState('bar');

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00c49f', '#ffbb28', '#ff8042'];

  if (loading) {
    return (
      <Box sx={{ height: 200, mt: 1 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        height: 200, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography variant="body2" color="text.secondary">
          No zip code data available
        </Typography>
      </Box>
    );
  }

  const displayData = data.slice(0, 8);

  return (
    <Box sx={{ height: 200, mt: 1 }}>
      {/* View Type Selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
        <Button
          size="small"
          variant={viewType === 'bar' ? 'contained' : 'outlined'}
          onClick={() => setViewType('bar')}
        >
          Bar
        </Button>
        <Button
          size="small"
          variant={viewType === 'pie' ? 'contained' : 'outlined'}
          onClick={() => setViewType('pie')}
        >
          Pie
        </Button>
        <Button
          size="small"
          variant={viewType === 'list' ? 'contained' : 'outlined'}
          onClick={() => setViewType('list')}
        >
          List
        </Button>
      </Box>

      {viewType === 'bar' && (
        <ResponsiveContainer width="100%" height="80%">
          <BarChart data={displayData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis 
              type="category" 
              dataKey="zipCode" 
              tick={{ fontSize: isMobile ? 10 : 12 }}
              width={50}
            />
            <Tooltip />
            <Bar dataKey="searchCount" fill="#8884d8" radius={[0, 4, 4, 0]}>
              {displayData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {viewType === 'pie' && (
        <ResponsiveContainer width="100%" height="80%">
          <PieChart>
            <Pie
              data={displayData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ zipCode, percent }) => `${zipCode}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={70}
              fill="#8884d8"
              dataKey="searchCount"
            >
              {displayData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      )}

      {viewType === 'list' && (
        <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
          <List dense>
            {displayData.map((item, index) => (
              <ListItem 
                key={item.zipCode} 
                divider={index < displayData.length - 1}
                sx={{ py: 0.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    color={index < 3 ? "primary" : "default"}
                    variant={index < 3 ? "filled" : "outlined"}
                    sx={{ mr: 1, minWidth: 28, height: 24 }}
                  />
                  <LocationOn color="action" sx={{ mr: 1, fontSize: '1rem' }} />
                  <ListItemText 
                    primary={item.zipCode}
                    secondary={`${item.searchCount} searches`}
                    primaryTypographyProps={{ 
                      fontWeight: 'medium',
                      fontSize: '0.875rem'
                    }}
                    secondaryTypographyProps={{ 
                      variant: 'caption'
                    }}
                  />
                </Box>
                <ListItemSecondaryAction>
                  <Button 
                    size="small"
                    onClick={() => onViewBookkeepers(item.zipCode)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    View
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default TopZipCodes;